import React, { RefObject, useEffect, useRef, useState } from 'react';
import BraintreeClient from 'braintree-web/client';
import BraintreeHostedFields from 'braintree-web/hosted-fields';
import BraintreeThreeDSecure from 'braintree-web/three-d-secure';
import { Field, Input, LoadingBalls, Overlay, Stack, Text } from '@moda/om';
import { UseUserQuery } from '../../../generated/types';
import { CardValidationError } from '../../../constants';
import { throwQueryError } from '../../../lib/apollo/throwQueryError';
import { useUser } from '../../../hooks/useUser';
import { BillingAddress, PaymentMethod } from '../../../hooks/usePaymentMethods';
import { useBraintreeClientToken } from '../../../hooks/useBraintreeClientToken';
import { sanitizePostalCodeForBraintree } from '../../../lib/sanitizePostalCodeForBraintree';
import { createMaskWithLastFour } from './createMaskWithLastFour';
import { PaymentMethodBraintreeInput } from './PaymentMethodBraintreeInput';

enum Mode {
  Resting,
  Loading
}

type FormErrors = { [key: string]: string };

export type CardData = {
  cardholderName: string;
  lastFour: string;
  cardType: string;
  expirationMonth: string;
  expirationYear: string;
  nonce: string;
  threeDSecureAuthenticationId?: string;
};

export type BraintreeCardFormHandle = {
  validate: () => boolean;
  submit: ({ address }: { address: BillingAddress }) => Promise<CardData>;
};

type Props = {
  ref?: RefObject<BraintreeCardFormHandle | null>;
  method?: PaymentMethod;
  amount?: string;
};

const LABELS: { [key: string]: string } = {
  cardholderName: "Card Holder's Name",
  number: 'Card Number',
  expirationMonth: 'Exp Month',
  expirationYear: 'Exp Year',
  cvv: 'Security Code'
};

const initForm = async ({
  amount = '0',
  method,
  user,
  token,
  containers,
  onValidationChange
}: {
  amount?: string;
  method?: PaymentMethod;
  user: UseUserQuery['user'];
  token: string;
  containers: {
    cardholderName: HTMLDivElement | null;
    number: HTMLDivElement | null;
    expirationMonth: HTMLDivElement;
    expirationYear: HTMLDivElement;
    cvv: HTMLDivElement;
  };
  onValidationChange: (errors: FormErrors) => void;
}) => {
  const client = await BraintreeClient.create({ authorization: token });
  const threeDS = await BraintreeThreeDSecure.create({ authorization: token, version: 2 });

  const hostedFields = await BraintreeHostedFields.create({
    client,
    fields: {
      ...(containers.cardholderName
        ? {
            cardholderName: {
              container: containers.cardholderName,
              prefill: method?.attributes.cardholderName
            }
          }
        : {}),
      ...(containers.number ? { number: { container: containers.number } } : {}),
      expirationMonth: {
        container: containers.expirationMonth,
        prefill: method?.attributes.expirationDate?.split('/')[0]
      },
      expirationYear: {
        container: containers.expirationYear,
        prefill: method?.attributes.expirationDate?.split('/')[1]
      },
      cvv: { container: containers.cvv }
    }
  });

  return {
    validate: () => {
      const state = hostedFields.getState();

      const errors = Object.entries(state.fields).reduce(
        (errors, [key, { isValid, isEmpty }]) => ({
          ...errors,
          [key]: isValid
            ? ''
            : isEmpty
              ? `${LABELS[key]} is required.`
              : `Please enter a valid ${LABELS[key]}.`
        }),
        {} as FormErrors
      );

      onValidationChange(errors);

      return Object.values(errors).every(value => !value);
    },
    submit: async ({ address }: { address: BillingAddress }) => {
      const { nonce, details } = await hostedFields.tokenize();

      const cardData: CardData = {
        cardholderName: details.cardholderName ?? method?.attributes.cardholderName ?? '',
        lastFour: details.lastFour,
        cardType: details.cardType,
        expirationMonth: details.expirationMonth,
        expirationYear: details.expirationYear,
        nonce
      };

      if (method) return cardData;

      // This is declared here because the types on `verifyCard` are incorrect and don't include `onLookupComplete`.
      const onLookupComplete = { onLookupComplete: (_: unknown, next: () => void) => next() };

      const { threeDSecureInfo, nonce: nonceFromThreeDS } = await threeDS.verifyCard({
        amount,
        nonce: nonce,
        bin: details.bin,
        email: user?.email ?? undefined,
        billingAddress: {
          countryCodeAlpha2: address.countryCode,
          extendedAddress: address.extendedAddress ?? undefined,
          givenName: user?.firstName ?? undefined,
          locality: address.locality,
          phoneNumber: user?.phone ?? undefined,
          postalCode: sanitizePostalCodeForBraintree(address.postalCode),
          region: address.region,
          streetAddress: address.streetAddress
        },
        collectDeviceData: true,
        ...onLookupComplete
      });

      if (!threeDSecureInfo.liabilityShifted)
        throw new CardValidationError("threeDSecure: liability hasn't shifted");

      return {
        ...cardData,
        nonce: nonceFromThreeDS,
        threeDSecureAuthenticationId: threeDSecureInfo.threeDSecureAuthenticationId
      };
    }
  };
};

export const PaymentMethodBraintreeCardForm: React.FC<Props> = ({ ref, method, amount }) => {
  const { user } = useUser();

  const isFormCreatedRef = useRef(false);
  const cardholderNameRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLDivElement>(null);
  const expirationMonthRef = useRef<HTMLDivElement>(null);
  const expirationYearRef = useRef<HTMLDivElement>(null);
  const cvvRef = useRef<HTMLDivElement>(null);

  const [mode, setMode] = useState(Mode.Loading);
  const [braintreeError, setBraintreeError] = useState<unknown>();
  const [errors, setErrors] = useState<FormErrors>({});

  const isNew = !method?.id;

  const { token, error: tokenError } = useBraintreeClientToken();

  useEffect(() => {
    if (
      !token ||
      !expirationMonthRef.current ||
      !expirationYearRef.current ||
      !cvvRef.current ||
      isFormCreatedRef.current
    )
      return;

    isFormCreatedRef.current = true;

    initForm({
      amount,
      method,
      user,
      token,
      containers: {
        cardholderName: cardholderNameRef.current,
        number: numberRef.current,
        expirationMonth: expirationMonthRef.current,
        expirationYear: expirationYearRef.current,
        cvv: cvvRef.current
      },
      onValidationChange: setErrors
    })
      .then(handle => {
        if (ref) ref.current = handle;
        setMode(Mode.Resting);
      })
      .catch(error => {
        setBraintreeError(error);
      });
  }, [amount, method, user, token, ref]);

  if (tokenError) throwQueryError(tokenError);

  if (braintreeError) throw braintreeError;

  return (
    <>
      <div className="PaymentMethodForm__name-number">
        {isNew ? (
          <>
            <Field
              className="PaymentMethodForm__input"
              label={LABELS.cardholderName}
              name="cardholderName"
              error={errors.cardholderName}
            >
              <PaymentMethodBraintreeInput ref={cardholderNameRef} />
            </Field>
            <Field
              className="PaymentMethodForm__input"
              label={LABELS.number}
              name="number"
              error={errors.number}
            >
              <PaymentMethodBraintreeInput ref={numberRef} />
            </Field>
          </>
        ) : (
          <>
            <Input
              className="PaymentMethodForm__input"
              label={LABELS.cardholderName}
              name="cardholderName"
              disabled
              defaultValue={method.attributes.cardholderName}
            />
            <Input
              className="PaymentMethodForm__input"
              label={LABELS.number}
              name="cardNumber"
              disabled
              defaultValue={createMaskWithLastFour(method)}
            />
          </>
        )}
      </div>
      <div className="PaymentMethodForm__expiration-cvv">
        <Stack className="PaymentMethodForm__expiration" direction="horizontal" space={4}>
          <Field
            className="PaymentMethodForm__input"
            label={LABELS.expirationMonth}
            name="expirationMonth"
            error={errors.expirationMonth}
          >
            <PaymentMethodBraintreeInput ref={expirationMonthRef} />
          </Field>
          <Field
            className="PaymentMethodForm__input"
            label={LABELS.expirationYear}
            name="expirationYear"
            error={errors.expirationYear}
          >
            <PaymentMethodBraintreeInput ref={expirationYearRef} />
          </Field>
        </Stack>
        <Field
          className="PaymentMethodForm__input"
          label={LABELS.cvv}
          name="cvv"
          error={errors.cvv}
        >
          <PaymentMethodBraintreeInput ref={cvvRef} />
        </Field>
      </div>
      <Overlay show={mode === Mode.Loading}>
        <LoadingBalls />

        <Text treatment="eyebrow">Loading</Text>
      </Overlay>
    </>
  );
};
